<script setup lang="ts">
import RadioInput from "~/components/Common/Inputs/RadioInput.vue";
import SessionTag from "~/components/Common/SessionTag.vue";
import { useSessions } from "~/composables/sessions/useSessions";
import { useCreateProjectStore } from "~/store/project/create-project.store";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useFormats } from "~/composables/utils/useFormat";
import { ItemType } from "@simbelapp/order-sdk";

const emit = defineEmits(["update:modelValue", "change"]);
const sessions = useSessions();
const createProjectCoreStore = useCreateProjectStore();
const { project } = storeToRefs(createProjectCoreStore);
const { formatTo2Decimals } = useFormats();
const formats = useFormats();
defineProps({
  modelValue: {
    type: Boolean,
  },
  data: {
    type: Object,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
</script>

<template>
  <div v-if="data">
    <div
      class="session-item cursor-pointer"
      :class="{ 'border !border-primary-blue-color': modelValue }"
      @click="emit('change', data?.sessionId, data?.projectId, data.status)"
    >
      <div class="left-side">
        <div class="flex justify-between items-center">
          <div class="flex gap-1 items-center">
            <SvgIcon name="date" class="w-4 h-4" />
            <div class="flex gap-1 items-center">
              <span class="body_med">du</span>
              <span class="body_med" :class="{ 'text-medium-grey-color': !data.startDate }">{{
                data.startDate ? formats.toDateDisplay(data.startDate) : "À définir"
              }}</span>
              <span v-if="data.startTime" class="body_med">à {{ data.startTime.substring(0, 5) }}</span>
              <span class="body_med">au</span>
              <span class="body_med" :class="{ 'text-medium-grey-color': !data.endDate }">{{
                data.endDate ? formats.toDateDisplay(data.endDate) : "À définir"
              }}</span>
              <span v-if="data.endTime" class="body_med">à {{ data.endTime.substring(0, 5) }}</span>
            </div>
          </div>
          <SessionTag v-if="data?.status !== ItemType.Project" validated :session-status="data.status"> </SessionTag>
          <SessionTag v-else project></SessionTag>
          <!-- <SessionTag v-else-if="data.itemType === ItemType.Session" validated :session-status="getSessionStatus(data.startDate, data.endDate)">
                </SessionTag> -->
        </div>
        <div class="flex gap-1 items-center">
          <SvgIcon name="pin" class="w-4 h-4 text-secondary-color" />
          <span v-if="data.trainingAddress" class="text-secondary-color truncate max-w-[420px]">{{
            data.trainingAddress
          }}</span>
          <span v-else-if="data.location" class="text-secondary-color truncate max-w-[420px]">{{ data.location }}</span>
          <span v-else class="text-medium-grey-color italic">{{ "À définir" }}</span>
        </div>
        <div class="flex gap-4 items-center w-full">
          <div class="flex gap-1 items-center">
            <SvgIcon name="student" class="w-4 h-4 text-secondary-color" />
            <span v-if="data.maxNbAttendees" class="text-secondary-color whitespace-nowrap">
              {{ data.nbAttendees }} / {{ data.maxNbAttendees }}
            </span>
            <span v-else class="text-secondary-color">
              {{ data.nbAttendees }}
            </span>
          </div>
          <div class="flex gap-1 items-center">
            <SvgIcon name="indiv" class="w-4 h-4 text-secondary-color" />
            <span v-if="data.sessionItemType" class="text-secondary-color whitespace-nowrap">{{
              sessions.sessionTypeMapping(data.sessionItemType)?.value
            }}</span>
            <span v-else class="text-medium-grey-color italic whitespace-nowrap">{{ "À définir" }}</span>
          </div>
          <div class="flex gap-1 items-center">
            <SvgIcon name="presential" class="w-4 h-4 text-secondary-color" />
            <span v-if="data.format" class="text-secondary-color whitespace-nowrap">{{
              sessions.sessionItemFormatMap(data.format)?.value
            }}</span>
            <span v-else class="text-medium-grey-color italic whitespace-nowrap">{{ "À définir" }}</span>
          </div>
          <div class="flex gap-1 items-center">
            <SvgIcon name="time_normal" class="w-4 h-4 text-secondary-color" />
            <span v-if="data.duration" class="text-secondary-color"
              >{{ formatTo2Decimals(Number(data.duration)) }}h</span
            >
            <span v-else-if="data.durationHours" class="text-secondary-color"
              >{{ formatTo2Decimals(Number(data.durationHours)) }}h</span
            >
            <span v-else-if="Number.isFinite(project.training.trainingId?.durationHours)" class="text-secondary-color">
              {{ formatTo2Decimals(Number(project.training.trainingId?.durationHours)) }}h</span
            >
            <span v-else class="text-medium-grey-color italic whitespace-nowrap">{{ "À définir" }}</span>
          </div>
          <div class="flex gap-1 items-center">
            <SvgIcon name="categorie" class="w-4 h-4 text-secondary-color" />
            <span v-if="data.designation" class="text-secondary-color truncate max-w-[100px]">{{
              data.designation
            }}</span>
            <span v-else class="text-medium-grey-color italic">{{ "À définir" }}</span>
          </div>
        </div>
      </div>
      <div class="right-side">
        <RadioInput
          class="min-w-[16px] min-h-[16px]"
          :checked="modelValue"
          :disabled="disabled"
          @click="
            () => {
              emit('update:modelValue', !modelValue);
              emit('change', data?.sessionId, data?.projectId, data?.status);
            }
          "
        >
        </RadioInput>
      </div>
    </div>
    <div
      v-if="modelValue && data.maxNbAttendees && data.nbAttendees >= data.maxNbAttendees"
      class="text-secondary-color flex items-center gap-2 mt-1"
    >
      <SvgIcon name="info" class="w-4 h-4"></SvgIcon>
      <span>Le nombre de places maximal a été atteint sur cette session.</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.session-item {
  @apply flex gap-4 rounded-lg p-5 border-stroke-color border;

  &:hover {
    @apply bg-lightest-grey-color;
  }
}

.left-side {
  @apply flex flex-col gap-1 flex-auto;
}

.right-side {
  @apply flex m-auto h-full;
}
</style>
